import { useAnalytics } from '@/composables/useAnalytics'
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { preloadCriticalQueries } from '@/queries/preloadCriticalQueries'
import { queryClient } from '@/queries/queryClient'
import { useAuthStore } from '@/stores/auth'

const Account = () => import('@/pages/Account/Account.vue')
const AccountOrderHistory = () => import('@/pages/AccountOrderHistory/AccountOrderHistory.vue')
const AddDog = () => import('@/pages/AddDog/AddDog.vue')
const AddDogSuccess = () => import('@/pages/AddDog/AddDogSuccess.vue')
const AddExtras = () => import('@/pages/AddExtras/AddExtras.vue')
const CancelPlanFeedback = () => import('@/pages/CancelPlanFeedback/CancelPlanFeedback.vue')
const ConfirmForgotPassword = () => import('@/pages/ConfirmForgotPassword/ConfirmForgotPassword.vue')
const Deliveries = () => import('@/pages/Deliveries/Deliveries.vue')
const Dogs = () => import('@/pages/Dogs/index.vue')
const EditAccountDetails = () => import('@/pages/EditAccountDetails/EditAccountDetailsLoader.vue')
const EditAccountPassword = () => import('@/pages/EditAccountPassword/EditAccountPassword.vue')
const EditDeliveryAddress = () => import('@/pages/EditDeliveryAddress/EditDeliveryAddressLoader.vue')
const EditDeliveryDate = () => import('@/pages/EditDeliveryDate/EditDeliveryDateLoader.vue')
const EditDogDetails = () => import('@/pages/EditDogDetails/EditDogDetailsLoader.vue')
const EditDogMealPlan = () => import('@/pages/EditDogMealPlan/EditDogMealPlan.vue')
const EditDogMealPlanResult = () => import('@/pages/EditDogMealPlan/EditDogMealPlanResult.vue')
const EditDogProfile = () => import('@/pages/EditDogProfile/EditDogProfileLoader.vue')
const EditExtras = () => import('@/pages/EditExtras/EditExtras.vue')
const EditMeals = () => import('@/pages/EditMeals/EditMeals.vue')
const EditServingSize = () => import('@/pages/EditServingSize/EditServingSizeLoader.vue')
const EditServingSizeResult = () => import('@/pages/EditServingSize/EditServingSizeResult.vue')
const EditServingSizeReview = () => import('@/pages/EditServingSize/EditServingSizeReview.vue')
const ForgotPassword = () => import('@/pages/ForgotPassword/ForgotPassword.vue')
const ForgotPasswordEmailSent = () => import('@/pages/ForgotPasswordEmailSent/ForgotPasswordEmailSent.vue')
const ReferAFriend = () => import('@/pages/ReferAFriend/ReferAFriend.vue')
const ReferralHistory = () => import('@/pages/ReferralHistory/ReferralHistory.vue')
const Login = () => import('@/pages/Login/Login.vue')
const ProductDetails = () => import('@/pages/ProductDetails/ProductDetailsPage.vue')
// const Shop = () => import('@/pages/Shop/Shop.vue')
const PageNotFound = () => import('@/pages/Error/PageNotFound.vue')

export const RouteName = {
  ACCOUNT_CANCEL_PLAN: 'account-cancel-plan',
  ACCOUNT_EDIT_ADDRESS: 'account-edit-address',
  ACCOUNT_EDIT_DETAILS: 'account-edit-details',
  ACCOUNT_EDIT_PASSWORD: 'account-edit-password',
  ACCOUNT_HOME: 'account-home',
  ACCOUNT_ORDER_HISTORY: 'account-order-history',
  CONFIRM_FORGOT_PASSWORD: 'confirm-forgot-password',
  DELIVERIES: 'deliveries',
  DOGS: 'dogs',
  DOGS_ADD: 'dogs-add',
  DOGS_ADD_RESULT: 'dogs-add-result',
  DOGS_EDIT_DETAILS: 'dogs-edit-details',
  DOGS_EDIT_PLAN: 'dogs-edit-plan',
  DOGS_EDIT_PLAN_RESULT: 'dogs-edit-plan-result',
  DOGS_EDIT_MEALS: 'dogs-edit-meals',
  DOGS_EDIT_PROFILE: 'dogs-edit-profile',
  DOGS_EDIT_SERVING_SIZE: 'dogs-edit-serving-size',
  DOGS_EDIT_SERVING_SIZE_REVIEW: 'dogs-edit-serving-size-review',
  DOGS_EDIT_SERVING_SIZE_RESULT: 'dogs-edit-serving-size-result',
  EMAIL_SENT: 'email-sent',
  FORGOT_PASSWORD: 'forgot-password',
  HOME: 'home',
  LOGIN: 'login',
  ORDER_EDIT_ADDRESS: 'deliveries-edit-address',
  ORDER_EDIT_DATE: 'deliveries-edit-date',
  ORDER_EDIT_MEALS: 'deliveries-edit-meals',
  ORDER_ADD_EXTRAS: 'deliveries-add-extras',
  ORDER_EDIT_EXTRAS: 'deliveries-edit-extras',
  ORDER_EXTRAS_PRODUCT: 'deliveries-extras-product',
  SHOP_HOME: 'shop-home',
  SHOP_PRODUCT: 'shop-product',
  NOT_FOUND: 'not-found',
  REFER_A_FRIEND: 'refer-a-friend',
  REFERRAL_HISTORY: 'referral-history',
} as const
export type RouteName = (typeof RouteName)[keyof typeof RouteName]

export const routes: RouteRecordRawWithMeta[] = [
  {
    path: '/',
    name: RouteName.HOME,
    redirect: { name: RouteName.DELIVERIES },
  },
  {
    path: '/login',
    name: RouteName.LOGIN,
    component: Login,
    meta: {
      unauthenticated: true,
      fullscreen: true,
    },
  },
  {
    path: '/forgot-password',
    name: RouteName.FORGOT_PASSWORD,
    component: ForgotPassword,
    meta: {
      unauthenticated: true,
      fullscreen: true,
    },
  },
  {
    path: '/confirm-forgot-password',
    name: RouteName.CONFIRM_FORGOT_PASSWORD,
    component: ConfirmForgotPassword,
    meta: {
      unauthenticated: true,
      fullscreen: true,
    },

    props: (route) => {
      return {
        confirmationCode: route.query.code,
        email: route.query.email,
      }
    },
  },
  {
    path: '/email-sent',
    name: RouteName.EMAIL_SENT,
    component: ForgotPasswordEmailSent,
    meta: {
      unauthenticated: true,
      fullscreen: true,
    },
  },
  {
    path: '/account/refer-a-friend',
    name: RouteName.REFER_A_FRIEND,
    component: ReferAFriend,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/account/referral-history',
    name: RouteName.REFERRAL_HISTORY,
    component: ReferralHistory,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/deliveries/:orderId?',
    name: RouteName.DELIVERIES,
    component: Deliveries,
    props: (route) => ({
      orderId: route.params.orderId,
    }),
  },
  {
    path: '/deliveries/:orderId/edit/date',
    name: RouteName.ORDER_EDIT_DATE,
    component: EditDeliveryDate,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      orderId: route.params.orderId,
    }),
  },
  {
    path: '/deliveries/:orderId/edit/address',
    name: RouteName.ORDER_EDIT_ADDRESS,
    component: EditDeliveryAddress,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      orderId: route.params.orderId,
    }),
  },
  {
    path: '/deliveries/:orderId/edit/meals/:animalId',
    name: RouteName.ORDER_EDIT_MEALS,
    component: EditMeals,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/deliveries/:orderId/edit/:productType(treat|supplement)',
    name: RouteName.ORDER_EDIT_EXTRAS,
    component: EditExtras,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      orderId: route.params.orderId,
      productType: route.params.productType,
    }),
  },
  {
    path: '/deliveries/:orderId/add/:productType(treat|supplement)',
    name: RouteName.ORDER_ADD_EXTRAS,
    component: AddExtras,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      orderId: route.params.orderId,
      productType: route.params.productType,
    }),
  },
  {
    path: '/deliveries/:orderId/extras/:productVariantId',
    name: RouteName.ORDER_EXTRAS_PRODUCT,
    component: ProductDetails,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      orderId: route.params.orderId,
      productVariantId: route.params.productVariantId,
      routeType: 'Extras',
    }),
  },
  {
    path: '/dogs',
    name: RouteName.DOGS,
    component: Dogs,
  },
  {
    path: '/dogs/add',
    name: RouteName.DOGS_ADD,
    component: AddDog,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/dogs/add/result',
    name: RouteName.DOGS_ADD_RESULT,
    component: AddDogSuccess,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/dogs/:animalId/edit/details',
    name: RouteName.DOGS_EDIT_DETAILS,
    component: EditDogDetails,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      animalId: route.params.animalId,
    }),
  },
  {
    path: '/dogs/:animalId/:feedingPlanId/:orderId/edit-serving-size',
    name: RouteName.DOGS_EDIT_SERVING_SIZE,
    component: EditServingSize,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      animalId: route.params.animalId,
    }),
  },
  {
    path: '/dogs/:animalId/:feedingPlanId/:orderId/edit-serving-size/review',
    name: RouteName.DOGS_EDIT_SERVING_SIZE_REVIEW,
    component: EditServingSizeReview,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/dogs/:animalId/:feedingPlanId/:orderId/edit-serving-size/result',
    name: RouteName.DOGS_EDIT_SERVING_SIZE_RESULT,
    component: EditServingSizeResult,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/dogs/:animalId/edit/plan',
    name: RouteName.DOGS_EDIT_PLAN,
    component: EditDogMealPlan,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      animalId: route.params.animalId,
    }),
  },
  {
    path: '/dogs/:animalId/edit/plan/result',
    name: RouteName.DOGS_EDIT_PLAN_RESULT,
    component: EditDogMealPlanResult,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      animalId: route.params.animalId,
    }),
  },
  {
    path: '/dogs/:animalId/edit/meals',
    name: RouteName.DOGS_EDIT_MEALS,
    component: EditMeals,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/dogs/:animalId/edit/profile',
    name: RouteName.DOGS_EDIT_PROFILE,
    component: EditDogProfile,
    meta: {
      fullscreen: true,
    },
    props: (route) => ({
      animalId: route.params.animalId,
    }),
  },
  // {
  //   path: '/shop',
  //   name: RouteName.SHOP_HOME,
  //   component: Shop,
  // },
  // {
  //   path: '/shop/:productId',
  //   name: RouteName.SHOP_PRODUCT,
  //   component: ProductDetails,
  //   meta: {
  //     fullscreen: true,
  //   },
  //   props: (route) => ({
  //     productId: route.params.productId,
  //     routeType: 'Shop',
  //   }),
  // },
  {
    path: '/account/edit/details',
    name: RouteName.ACCOUNT_EDIT_DETAILS,
    component: EditAccountDetails,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/account/edit/password',
    name: RouteName.ACCOUNT_EDIT_PASSWORD,
    component: EditAccountPassword,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/account/order-history',
    name: RouteName.ACCOUNT_ORDER_HISTORY,
    component: AccountOrderHistory,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/account/cancel-plan',
    name: RouteName.ACCOUNT_CANCEL_PLAN,
    component: CancelPlanFeedback,
    meta: {
      fullscreen: true,
    },
  },
  {
    path: '/account',
    name: RouteName.ACCOUNT_HOME,
    component: Account,
  },
  {
    path: '/account/edit/address',
    name: RouteName.ACCOUNT_EDIT_ADDRESS,
    component: EditDeliveryAddress,
    meta: {
      fullscreen: true,
    },
    props: () => {
      return {
        returnRoute: RouteName.ACCOUNT_HOME,
      }
    },
  },
  {
    // 404 route - must be last
    path: '/:catchAll(.*)',
    name: RouteName.NOT_FOUND,
    props: { buttonText: 'Go to deliveries', buttonTo: { name: RouteName.DELIVERIES } },
    component: PageNotFound,
    meta: {
      fullscreen: true,
    },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
      }
    } else {
      return { top: 0 }
    }
  },
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  if (!authStore.isAuthenticated && !to.meta.unauthenticated) {
    // No session in memory and the destination requires one — try to recover from a refresh cookie before bouncing to login.
    await authStore.checkIfTokenNeedRefresh()
  } else {
    await authStore.checkIfTokenExpired()
  }

  if (authStore.isAuthenticated && to.name === RouteName.LOGIN) {
    return { name: RouteName.DELIVERIES }
  }

  if (!authStore.isAuthenticated && !to.meta.unauthenticated) {
    return { name: RouteName.LOGIN }
  }

  if (authStore.isAuthenticated && !to.meta.unauthenticated) {
    await preloadCriticalQueries(queryClient).catch(() => {
      // Let the route resolve so App.vue can show the critical-data error state
      // with a retry action instead of leaving the router permanently pending.
    })
  }
})

router.afterEach((to) => {
  if (to.name) {
    useAnalytics().page(String(to.name))
  }
})

type RouteRecordRawWithMeta = RouteRecordRaw & {
  name: RouteName
  meta?: {
    fullscreen?: boolean
    unauthenticated?: boolean
  }
}

export default router
